<app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-3xl mx-4 sm:mx-6">
  @if (meeting) {
  <div class="p-6 sm:p-8">
    <!-- Header -->
    <div class="pr-10 mb-6">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">{{ meeting.title }}</h2>
        <div class="flex flex-wrap gap-1.5">
          <app-badge [color]="getStatusColor()">{{ getStatusLabel() }}</app-badge>
          @if (meeting.isNoShow) {
            <app-badge color="error">No-show</app-badge>
          }
          @if (meeting.paymentStatus) {
            <app-badge [color]="getPaymentColor(meeting.paymentStatus)">
              {{ paymentStatusLabel(meeting.paymentStatus) }}
              @if (meeting.paymentAmount) { · {{ meeting.paymentAmount }} }
            </app-badge>
          }
          @if (meeting.source && meeting.source !== 'manual') {
            <app-badge color="light">{{ sourceLabel(meeting.source) }}</app-badge>
          }
          @if (meeting.rescheduleCount && meeting.rescheduleCount > 0) {
            <app-badge color="warning">Reprog. {{ meeting.rescheduleCount }}</app-badge>
          }
        </div>
      </div>
      @if (meeting.description) {
        <p class="text-base text-gray-600 dark:text-gray-400 mt-2">{{ meeting.description }}</p>
      }
      <div class="flex flex-wrap items-center gap-2 mt-3">
        <app-badge [color]="getTypeColor()">{{ getTypeLabel() }}</app-badge>
        <app-badge color="info">{{ formatFechaCompleta(meeting.datetimeStart) }}</app-badge>
        <!-- INC 83: salida a Editar desde el detalle (la notificación ya no entra
             directo al formulario). -->
        <button type="button" (click)="editarReunion()"
          *appHasPermission="'meetings:edit'"
          class="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-white/[0.05]">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
            </path>
          </svg>
          Editar
        </button>
      </div>
    </div>

    <!-- Tabs nav -->
    <div class="border-b border-gray-200 dark:border-gray-700 mb-5">
      <nav class="flex gap-1 -mb-px overflow-x-auto">
        @for (tab of [
          {key: 'overview', label: 'Resumen'},
          {key: 'participants', label: 'Participantes'},
          {key: 'agenda', label: 'Agenda'},
          {key: 'reminders', label: 'Recordatorios'},
          {key: 'logs', label: 'Historial'}
        ]; track tab.key) {
          <button type="button" (click)="setTab($any(tab.key))"
            class="px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
            [class.border-brand-500]="activeTab === tab.key"
            [class.text-brand-600]="activeTab === tab.key"
            [class.dark:text-brand-400]="activeTab === tab.key"
            [class.border-transparent]="activeTab !== tab.key"
            [class.text-gray-500]="activeTab !== tab.key"
            [class.hover:text-gray-700]="activeTab !== tab.key"
            [class.dark:text-gray-400]="activeTab !== tab.key">
            {{ tab.label }}
          </button>
        }
      </nav>
    </div>

    <!-- ═══════════ Tab: Overview ═══════════ -->
    @if (activeTab === 'overview') {
    <div class="space-y-6">
      <div>
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Detalles de la Reunión</h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Organizador</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getOrganizerName() }}</p>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 01.586 1.414V19a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z"/>
            </svg>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Tipo</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getTypeLabel() }}</p>
            </div>
          </div>
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Estado</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getStatusLabel() }}</p>
            </div>
          </div>
          @if (meeting.videoProvider) {
          <div class="flex items-start gap-3">
            <svg class="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Video</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ videoProviderLabel(meeting.videoProvider) }}</p>
            </div>
          </div>
          }
        </div>
      </div>

      <!-- Relaciones -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Relaciones</h3>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400">Contacto</p>
            <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getContactName() }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400">Empresa</p>
            <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getCompanyName() }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500 dark:text-gray-400">Negocio</p>
            <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getDealName() }}</p>
          </div>
        </div>
      </div>

      <!-- Respuestas a los campos adicionales de la página de reserva -->
      @if (bookingAnswers.length > 0) {
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Respuestas del formulario de reserva</h3>
        <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          @for (a of bookingAnswers; track $index) {
          <div class="min-w-0">
            <dt class="text-xs text-gray-500 dark:text-gray-400">{{ a.label }}</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white whitespace-pre-wrap break-words">{{ a.value }}</dd>
          </div>
          }
        </dl>
      </div>
      }

      @if (meeting.location || meeting.locationMapUrl || meeting.meetingUrl || meeting.videoJoinUrl) {
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Ubicación</h3>
        <div class="space-y-2">
          @if (meeting.location) {
            <p class="text-sm text-gray-900 dark:text-white">{{ meeting.location }}</p>
          }
          @if (meeting.locationMapUrl) {
            <a [href]="meeting.locationMapUrl" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 break-all">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              Ver en Google Maps
            </a>
          }
          @if (canJoinJitsi()) {
            <button type="button" (click)="joinJitsi()"
              class="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              Unirse a la videollamada
            </button>
          }
          @if (meeting.videoJoinUrl) {
            <a [href]="meeting.videoJoinUrl" target="_blank" class="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 break-all block">
              {{ meeting.videoJoinUrl }}
            </a>
          } @else if (meeting.meetingUrl) {
            <a [href]="meeting.meetingUrl" target="_blank" class="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 break-all block">
              {{ meeting.meetingUrl }}
            </a>
          }
        </div>
      </div>
      }

      @if (meeting.notes || meeting.outcome || meeting.cancellationReasonText) {
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-6">
        @if (meeting.notes) {
        <div>
          <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Notas</h3>
          <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ meeting.notes }}</p>
        </div>
        }
        @if (meeting.outcome) {
        <div>
          <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Resultado</h3>
          <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ meeting.outcome }}</p>
        </div>
        }
        @if (meeting.cancellationReasonText) {
        <div class="sm:col-span-2">
          <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Motivo de cancelación</h3>
          <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ meeting.cancellationReasonText }}</p>
        </div>
        }
      </div>
      }

      <div class="pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Inicio</p>
          <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatFechaCompleta(meeting.datetimeStart) }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Fin</p>
          <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatFechaCompleta(meeting.datetimeEnd) }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Creada</p>
          <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatFecha(meeting.datetimeCreated) }}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400">Actualizada</p>
          <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatFecha(meeting.datetimeUpdated) }}</p>
        </div>
      </div>
    </div>
    }

    <!-- ═══════════ Tab: Participants ═══════════ -->
    @if (activeTab === 'participants') {
    <div>
      @if (loadingParticipants) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Cargando participantes...</p>
      } @else if (participants.length === 0) {
        <p class="text-sm text-gray-500 dark:text-gray-400">No hay participantes registrados.</p>
      } @else {
        <ul class="divide-y divide-gray-200 dark:divide-gray-700">
          @for (p of participants; track p.id) {
          <li class="py-3 flex items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ getParticipantDisplayName(p) }}</p>
              @if (p.email) { <p class="text-xs text-gray-500 dark:text-gray-400">{{ p.email }}</p> }
              @if (p.phone) { <p class="text-xs text-gray-500 dark:text-gray-400">{{ p.phone }}</p> }
              @if (p.notes) { <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ p.notes }}</p> }
            </div>
            <div class="flex flex-col items-end gap-1 flex-shrink-0">
              <app-badge [color]="getParticipantRoleColor(p)">{{ getParticipantRoleLabel(p) }}</app-badge>
              @if (p.status) {
                <app-badge [color]="getParticipantStatusColor(p.status)">{{ participantStatusLabel(p.status) }}</app-badge>
              }
              @if (p.isNoShow) {
                <app-badge color="error">No-show</app-badge>
              }
            </div>
          </li>
          }
        </ul>
      }
    </div>
    }

    <!-- ═══════════ Tab: Agenda ═══════════ -->
    @if (activeTab === 'agenda') {
    <div>
      <!-- Toolbar -->
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agenda</h3>
        @if (!showAgendaForm) {
          <button type="button" (click)="openNewAgenda()"
            class="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Agregar item
          </button>
        }
      </div>

      <!-- Formulario inline -->
      @if (showAgendaForm) {
      <form (ngSubmit)="saveAgenda()" class="mb-5 space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Título <span class="text-error-500">*</span></label>
          <input type="text" name="agendaTitle" [(ngModel)]="agendaModel.title" maxlength="200" required
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none"
            placeholder="Ej. Presentación de la propuesta" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Descripción</label>
          <textarea name="agendaDescription" [(ngModel)]="agendaModel.description" rows="2"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none"></textarea>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Orden</label>
            <input type="number" name="agendaOrder" [(ngModel)]="agendaModel.order" min="1"
              class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Duración (min)</label>
            <input type="number" name="agendaDuration" [(ngModel)]="agendaModel.durationMinutes" min="1"
              class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Presentador</label>
            <input type="text" name="agendaPresenter" [(ngModel)]="agendaModel.presenter" maxlength="100"
              class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none" />
          </div>
        </div>
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" (click)="cancelAgendaForm()"
            class="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Cancelar
          </button>
          <button type="submit" [disabled]="savingAgenda"
            class="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition-colors">
            {{ editingAgendaId ? 'Guardar cambios' : 'Agregar' }}
          </button>
        </div>
      </form>
      }

      @if (loadingAgenda) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Cargando agenda...</p>
      } @else if (agendaItems.length === 0 && !showAgendaForm) {
        <p class="text-sm text-gray-500 dark:text-gray-400">No hay items de agenda.</p>
      } @else if (agendaItems.length > 0) {
        <ol class="space-y-3">
          @for (item of agendaItems; track item.id; let idx = $index) {
          <li class="flex gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <span class="flex-shrink-0 w-7 h-7 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 text-sm font-semibold flex items-center justify-center">
              {{ item.order ?? idx + 1 }}
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-2">
                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ item.title }}</p>
                <div class="flex items-center gap-2 flex-shrink-0">
                  @if (item.durationMinutes) {
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ item.durationMinutes }} min</span>
                  }
                  <button type="button" (click)="openEditAgenda(item)" title="Editar"
                    class="text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button type="button" (click)="deleteAgenda(item)" title="Eliminar"
                    class="text-gray-400 hover:text-error-600 dark:hover:text-error-400 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
              @if (item.presenter) {
                <p class="text-xs text-gray-500 dark:text-gray-400">Presentador: {{ item.presenter }}</p>
              }
              @if (item.description) {
                <p class="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{{ item.description }}</p>
              }
            </div>
          </li>
          }
        </ol>
      }
    </div>
    }

    <!-- ═══════════ Tab: Reminders ═══════════ -->
    @if (activeTab === 'reminders') {
    <div>
      <!-- Toolbar -->
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Recordatorios</h3>
        @if (!showReminderForm) {
          <button type="button" (click)="openNewReminder()"
            class="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-600">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Agregar recordatorio
          </button>
        }
      </div>

      <!-- Formulario inline -->
      @if (showReminderForm) {
      <form (ngSubmit)="saveReminder()" class="mb-5 space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Antelación</label>
          <div class="flex flex-wrap gap-1.5 mb-2">
            @for (preset of reminderMinutePresets; track preset.value) {
              <button type="button" (click)="setReminderMinutes(preset.value)"
                class="rounded-lg px-2.5 py-1 text-xs font-medium border transition-colors"
                [class.bg-brand-500]="reminderModel.reminderMinutesBefore === preset.value"
                [class.text-white]="reminderModel.reminderMinutesBefore === preset.value"
                [class.border-brand-500]="reminderModel.reminderMinutesBefore === preset.value"
                [class.border-gray-300]="reminderModel.reminderMinutesBefore !== preset.value"
                [class.dark:border-gray-600]="reminderModel.reminderMinutesBefore !== preset.value"
                [class.text-gray-700]="reminderModel.reminderMinutesBefore !== preset.value"
                [class.dark:text-gray-300]="reminderModel.reminderMinutesBefore !== preset.value">
                {{ preset.label }}
              </button>
            }
          </div>
          <div class="flex items-center gap-2">
            <input type="number" name="reminderMinutes" [(ngModel)]="reminderModel.reminderMinutesBefore" min="1"
              class="w-28 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none" />
            <span class="text-xs text-gray-500 dark:text-gray-400">minutos antes de la reunión</span>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Método</label>
            <select name="reminderMethod" [(ngModel)]="reminderModel.reminderMethod"
              class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none">
              @for (opt of reminderMethodOptions; track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
              }
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Destinatario</label>
            <select name="reminderTarget" [(ngModel)]="reminderModel.reminderTarget"
              class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none">
              @for (opt of reminderTargetOptions; track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
              }
            </select>
          </div>
        </div>

        <!-- INC 72: plantilla de correo (solo cuando el método incluye correo) -->
        @if (reminderUsesEmail) {
          <div>
            <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Plantilla de correo</label>
            @if (reminderTemplates.length > 0) {
              <select name="reminderTemplate" [(ngModel)]="reminderModel.templateId"
                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-brand-500 focus:outline-none">
                <option [ngValue]="null">Plantilla por defecto</option>
                @for (tpl of reminderTemplates; track tpl.id) {
                  <option [ngValue]="tpl.id">{{ tpl.name }}</option>
                }
              </select>
              <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">Se enviará con esta plantilla. "Por defecto" usa la del sistema.</p>
            } @else if (reminderTemplatesError) {
              <!-- Un fallo de carga NO debe verse como "no hay plantillas": si no, el
                   usuario cree que perdió sus plantillas y el incidente es indiagnosticable. -->
              <p class="text-xs text-error-500">
                No se pudieron cargar las plantillas. Se usará el correo por defecto si guardas así.
              </p>
              <button type="button" (click)="reintentarPlantillas()"
                class="mt-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline text-left">
                Reintentar
              </button>
            } @else {
              <!-- El selector se alimenta SOLO de Configuración de Reuniones → Plantillas
                   de email. Las plantillas del módulo de marketing (#/templates) NO cuentan,
                   así que el vacío debe decir dónde crearlas para no parecer un error. -->
              <p class="text-xs text-gray-500 dark:text-gray-400">
                Aún no hay plantillas de reuniones. Se usará el correo por defecto del sistema.
              </p>
              <button type="button" (click)="irAConfigPlantillas()"
                class="mt-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline text-left">
                Crear una en Configuración de Reuniones → Plantillas de email
              </button>
            }
          </div>
        }
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" (click)="cancelReminderForm()"
            class="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
            Cancelar
          </button>
          <button type="submit" [disabled]="savingReminder"
            class="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition-colors">
            {{ editingReminderId ? 'Guardar cambios' : 'Agregar' }}
          </button>
        </div>
      </form>
      }

      @if (loadingReminders) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Cargando recordatorios...</p>
      } @else if (reminders.length === 0 && !showReminderForm) {
        <p class="text-sm text-gray-500 dark:text-gray-400">No hay recordatorios configurados.</p>
      } @else if (reminders.length > 0) {
        <ul class="divide-y divide-gray-200 dark:divide-gray-700">
          @for (r of reminders; track r.id) {
          <li class="py-3 flex items-center justify-between gap-3">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ formatReminderMinutes(r.reminderMinutesBefore) }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ getReminderMethodLabel(r.reminderMethod) }} · {{ getReminderTargetLabel(r.reminderTarget) }}
                @if (getReminderTemplateName(r); as tplName) {
                  · {{ tplName }}
                }
              </p>
              <!-- INC 73: enviado (con fecha) solo si SENT -->
              @if (getReminderStatus(r) === 'SENT' && r.datetimeSent) {
                <p class="text-xs text-gray-500 dark:text-gray-400">Enviado: {{ formatFechaCompleta(r.datetimeSent) }}</p>
              }
              <!-- INC 73: motivo del fallo si FAILED -->
              @if (getReminderStatus(r) === 'FAILED' && r.failureReason) {
                <p class="text-xs text-error-600 dark:text-error-400">Motivo: {{ r.failureReason }}</p>
              }
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <!-- INC 73: chip con el estado real (Pendiente / Enviado / Fallido) -->
              <app-badge [color]="getReminderStatusColor(r)">
                <span [title]="getReminderStatus(r) === 'FAILED' ? (r.failureReason || 'Fallido') : ''">
                  {{ getReminderStatusLabel(r) }}
                </span>
              </app-badge>
              <button type="button" (click)="openEditReminder(r)" title="Editar"
                class="text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </button>
              <button type="button" (click)="deleteReminder(r)" title="Eliminar"
                class="text-gray-400 hover:text-error-600 dark:hover:text-error-400 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </button>
            </div>
          </li>
          }
        </ul>
      }
    </div>
    }

    <!-- ═══════════ Tab: Logs ═══════════ -->
    @if (activeTab === 'logs') {
    <div>
      @if (loadingLogs) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Cargando historial...</p>
      } @else if (rescheduleLogs.length === 0 && timelineLogs.length === 0) {
        <p class="text-sm text-gray-500 dark:text-gray-400">Sin actividad registrada para esta reunión.</p>
      } @else {
        <!-- Actividad estándar (INC 47) — mismo timeline que Tickets/Negocios -->
        @if (timelineLogs.length > 0) {
          <h5 class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Actividad</h5>
          <app-audit-timeline
            [logs]="timelineLogs"
            [loading]="false"
            emptyMessage="Sin actividad registrada para esta reunión."
          />
        }

        <!-- Reprogramaciones -->
        @if (rescheduleLogs.length > 0) {
          <h5 class="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Reprogramaciones</h5>
          <ul class="space-y-3">
            @for (log of rescheduleLogs; track log.id) {
            <li class="p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between gap-2 mb-1">
                <app-badge [color]="getLogActionColor(log.action)">{{ getLogActionLabel(log.action) }}</app-badge>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatFechaCompleta(log.datetimeCreated) }}</span>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                De <span class="font-medium text-gray-700 dark:text-gray-300">{{ formatFechaCompleta(log.originalDatetimeStart) }}</span>
                @if (log.newDatetimeStart) {
                  → <span class="font-medium text-gray-700 dark:text-gray-300">{{ formatFechaCompleta(log.newDatetimeStart) }}</span>
                }
              </p>
              @if (log.reasonText) {
                <p class="text-sm text-gray-700 dark:text-gray-300 mt-1">{{ log.reasonText }}</p>
              }
              @if (log.initiatedBy) {
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Iniciado por: {{ log.initiatedBy }}</p>
              }
            </li>
            }
          </ul>
        }
      }
    </div>
    }
  </div>
  }
</app-modal>

<!-- Videollamada Jitsi inline (organizador). Hermano del modal: usa z-modal-top
     para quedar por encima del detail-modal (z-modal). -->
@if (jitsiOpen && meeting) {
  <app-jitsi-room
    [roomName]="jitsiRoomName()"
    [displayName]="jitsiDisplayName()"
    [email]="jitsiEmail()"
    [title]="meeting.title"
    [datetimeStart]="meeting.datetimeStart"
    (close)="closeJitsi()">
  </app-jitsi-room>
}
